import { prepareDateTimeForHttpRequest } from 'helpers/dateHelper';
import { normalizeTeamSearchQueryForApi } from 'helpers/stringHelper';
import { axios } from 'setup/axiosSetup';
import { SportIDs } from 'types/enums';
import { League } from 'types/League';
import { LiveEvent } from 'types/LiveEvent';
import { MatchOffer } from 'types/MatchOffer';
import {
  hasLiveEventStarted,
  mapLiveEvent,
  mapSportEvents,
  splitEventsByLeague,
  splitEventsBySport,
  splitLiveEventsBySport,
} from 'utils/sportEvent.utils';

export const getSports = async (
  toTime?: Date,
  options?: { results?: boolean; fromTime?: Date }
) => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

  try {
    const params = new URLSearchParams();
    if (options?.results) {
      params.set('results', '1');
    } else {
      if (toTime) params.set('to_time', prepareDateTimeForHttpRequest(toTime));
      if (options?.fromTime) {
        params.set(
          'from_time',
          String(Math.floor(options.fromTime.getTime() / 1000))
        );
      }
    }
    const qs = params.toString();
    const response = await axios.get(
      `${baseUrl}/get_sports${qs ? `?${qs}` : ''}`,
      { cache: options?.results ? false : undefined }
    );
    return response.data.sports.filter((x: any) => !!x.count);
  } catch (e) {
    console.error(e);
    return null;
  }
};

/** Map API `groups` to nested sidebar rows (SportListItem → List → Listitem). */
function buildNestedLeagueListFromGroups(
  groups: Array<{
    id: string;
    name: string;
    kind: string;
    cc?: string;
    count: number;
    leagues: Array<{
      name: string;
      display_name?: string;
      id: number;
      count: number;
      priority: number;
      cc?: string;
    }>;
  }>,
  sportId: number
): any[] {
  return groups.map((g, idx) => ({
    name: g.name,
    // The API's group key (ISO country code, "__intl__"): names the row in
    // the player's language (helpers/leagueGroupName).
    groupCode: g.id,
    id: -(sportId * 100000 + idx + 1),
    count: g.count,
    cc: g.cc,
    isLief: false,
    subList: (g.leagues || []).map((L) => ({
      name: L.display_name || L.name,
      id: L.id,
      count: L.count,
      priority: L.priority,
      cc: L.cc,
      isLief: true,
    })),
  }));
}

export const getLeagues = async (sportId: number, toTime?: Date) => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

  try {
    const response = await axios.get(
      `${baseUrl}/get_leagues?sport_id=${sportId}${toTime ? `&to_time=${prepareDateTimeForHttpRequest(toTime)}` : ''}`
    );
    // The offer order set in the admin (country groups, leagues within them),
    // as the classic league list shows it; `priority` alone ties most top
    // leagues and would put them alphabetically.
    const groups = response.data.groups;
    if (Array.isArray(groups) && groups.length > 0) {
      return groups.flatMap((g: { leagues?: League[] }) => g.leagues ?? []);
    }
    return response.data.leagues.sort(
      (a: League, b: League) => a.priority - b.priority
    );
  } catch (e) {
    console.error(e);
    return null;
  }
};

/** Desktop sidebar: country → leagues when API returns `groups`; else flat leagues. */
export const getLeaguesForSidebar = async (
  sportId: number,
  toTime?: Date,
  results?: boolean,
  fromTime?: Date
) => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

  try {
    const params = new URLSearchParams();
    params.set('sport_id', String(sportId));
    if (results) {
      params.set('results', '1');
    } else {
      if (toTime) params.set('to_time', prepareDateTimeForHttpRequest(toTime));
      if (fromTime) {
        params.set('from_time', String(Math.floor(fromTime.getTime() / 1000)));
      }
    }
    const response = await axios.get(
      `${baseUrl}/get_leagues?${params.toString()}`,
      {
        cache: results ? false : undefined,
      }
    );
    const data = response.data;
    if (data.groups && Array.isArray(data.groups) && data.groups.length > 0) {
      return buildNestedLeagueListFromGroups(data.groups, sportId);
    }
    return data.leagues.sort((a: League, b: League) => a.priority - b.priority);
  } catch (e) {
    console.error(e);
    return null;
  }
};

/** `mainOnly`: the main market's odds only, the other markets by name (chips
 *  and "+N" counts as usual) - a tenth of the full list, for the first paint;
 *  the rows come back with `marketsPending` set. */
export const getEventsByLeague = async (
  leagueId: number,
  sportId: SportIDs,
  toTime?: Date,
  fromTime?: Date,
  mainOnly = false
): Promise<MatchOffer[] | null> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

  try {
    const response = await axios.get(
      `${baseUrl}/get_events_for_league?league_id=${leagueId}${toTime ? `&to_time=${Math.floor(toTime.getTime() / 1000)}` : ''}${fromTime ? `&from_time=${Math.floor(fromTime.getTime() / 1000)}` : ''}${mainOnly ? '&markets=main' : ''}`
    );
    const events = mapSportEvents([...response.data.events], sportId);
    if (mainOnly) {
      for (const e of events) e.marketsPending = true;
    }
    return events;
  } catch (e) {
    console.error(e);
    return null;
  }
};

/** Results: optional `includeFis` pulls old rows outside the date window + syncs provider first. */
export const getResultsEventsByLeague = async (
  leagueId: number,
  sportId: SportIDs,
  resultsDetail?: boolean,
  includeFis?: number[]
): Promise<MatchOffer[] | null> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

  try {
    const qs = new URLSearchParams();
    qs.set('league_id', String(leagueId));
    qs.set('results', '1');
    if (resultsDetail) {
      qs.set('results_detail', '1');
    }
    if (includeFis?.length) {
      qs.set('include_fi', includeFis.map(String).join(','));
    }
    const response = await axios.get(
      `${baseUrl}/get_events_for_league?${qs.toString()}`,
      { cache: false }
    );
    return mapSportEvents([...response.data.events], sportId);
  } catch (e) {
    console.error(e);
    return null;
  }
};

/** Landing Today/Yesterday tabs: full event rows for one local calendar day (server interprets `day`). */
export const getResultsDayEvents = async (
  dayYyyymmdd: string
): Promise<MatchOffer[] | null> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

  try {
    const response = await axios.get(
      `${baseUrl}/get_results_day_events?day=${encodeURIComponent(dayYyyymmdd)}`,
      { cache: false }
    );
    const raw = response.data?.events;
    if (!Array.isArray(raw)) {
      return [];
    }
    const out: MatchOffer[] = [];
    for (const e of raw) {
      const sid = e?.league?.sport;
      if (sid == null) continue;
      const row = mapSportEvents([e], sid as SportIDs)[0];
      if (row) {
        out.push(row);
      }
    }
    return out;
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const getEventByid = async (
  eventId: number,
  options?: { syncResult?: boolean }
) => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

  try {
    const qs = new URLSearchParams();
    qs.set('event_id', String(eventId));
    if (options?.syncResult) {
      qs.set('sync_result', '1');
    }
    const response = await axios.get(
      `${baseUrl}/prematch_odds_by_id?${qs.toString()}`
    );
    console.log(response.data);
    return mapSportEvents(
      response.data.events,
      response.data.events[0].league.sport
    )[0];
  } catch (e) {
    console.error(e);
    return null;
  }
};

/** One-off provider result → DB (manual / old fixtures). */
export const syncResultForFi = async (
  fi: number
): Promise<{ ok: boolean; detail?: string } | null> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';
  try {
    const response = await axios.get(
      `${baseUrl}/sync_result_for_fi?fi=${encodeURIComponent(String(fi))}`,
      { cache: false }
    );
    return { ok: !!response.data?.ok, detail: response.data?.detail };
  } catch (e) {
    console.error(e);
    return null;
  }
};

/** Prematch rows for live-betting “upcoming” (main markets via mapSportEvents). */
export interface LiveUpcomingEventsPage {
  events: MatchOffer[];
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
  /** Events of the time window per sport, before the sport filter. */
  sportCounts: { sportId: number; count: number }[];
}

export const getLiveUpcomingEvents = async (
  fromSec: number,
  toSec: number,
  page: number = 1,
  pageSize: number = 100,
  sportId?: number
): Promise<LiveUpcomingEventsPage | null> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

  try {
    const response = await axios.get(
      `${baseUrl}/get_live_upcoming_events?from_time=${fromSec}&to_time=${toSec}&page=${page}&page_size=${pageSize}` +
        (sportId ? `&sport_id=${sportId}` : '')
    );
    const raw = response.data?.events ?? [];
    const mapped = (Array.isArray(raw) ? raw : [])
      .filter((e: any) => e?.league?.sport != null)
      .map((e: any) => mapSportEvents([e], e.league.sport as SportIDs)[0])
      .filter(Boolean);
    return {
      events: mapped,
      count: Number(response.data?.count ?? mapped.length),
      page: Number(response.data?.page ?? page),
      pageSize: Number(response.data?.page_size ?? pageSize),
      totalPages: Number(response.data?.total_pages ?? 1),
      sportCounts: (Array.isArray(response.data?.sport_counts)
        ? response.data.sport_counts
        : []
      ).map((x: any) => ({
        sportId: Number(x.sport_id),
        count: Number(x.count),
      })),
    };
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const getLiveSoccerEvents = async () => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

  try {
    const response = await axios.get(`${baseUrl}/get_live_events`, {
      cache: false,
    });
    return response.data['Live events'] ?? [];
  } catch (e) {
    console.error(e);
    return null;
  }
};

/** Live matches per sport, for the sport rail before the lists have loaded. */
export const getLiveCounts = async (
  sportIds: number[]
): Promise<Map<number, number>> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';
  try {
    const response = await axios.get(
      `${baseUrl}/live_counts?sport_ids=${sportIds.join(',')}`
    );
    return new Map(
      Object.entries(response.data?.counts ?? {}).map(([id, n]) => [
        Number(id),
        Number(n),
      ])
    );
  } catch (e) {
    console.error(e);
    return new Map();
  }
};

export const getLiveEvents = async (sportId: number) => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

  try {
    const response = await axios.get(
      `${baseUrl}/get_live_events?sport_id=${sportId}`,
      {
        cache: false,
      }
    );
    return response.data['Live events'] ?? [];
  } catch (e) {
    console.error(e);
    return null;
  }
};

/** `mainOnly` asks for the 1 X 2 market alone, for lists that show nothing
 *  else (the full events carry ~70 markets each). */
export const getHighlights = async (mainOnly = false) => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

  try {
    const response = await axios.get(`${baseUrl}/get_highlights`, {
      params: mainOnly ? { markets: 'main' } : undefined,
    });
    return mapSportEvents(response.data.events, SportIDs.SOCCER);
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const getLastMinuteEvents = async () => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

  try {
    const response = await axios.get(`${baseUrl}/get_last_minute_events`);
    const splittedEvents = splitEventsBySport(response.data.events);
    const events: MatchOffer[] = [];

    for (let [key, value] of splittedEvents) {
      events.push(...mapSportEvents(value, key));
    }

    return events;
  } catch (e) {
    console.error(e);
    return null;
  }
};

export type PopularSlipLeg = { fi: number; odd_custom_id: string };
export type PopularSlip = { played: number; legs: PopularSlipLeg[] };

/** "Most played bet slips": open tickets' picks counted together, topped up
 *  with suggestions, and the matches they are on. */
export const getPopularSlips = async (): Promise<{
  slips: PopularSlip[];
  events: MatchOffer[];
} | null> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

  try {
    const response = await axios.get(`${baseUrl}/popular_slips`);
    const events: MatchOffer[] = [];
    for (let [key, value] of splitEventsBySport(response.data.events ?? [])) {
      events.push(...mapSportEvents(value, key));
    }
    return { slips: response.data.slips ?? [], events };
  } catch (e) {
    console.error(e);
    return null;
  }
};

export type SharedSlipResponse = {
  code: string;
  slips: PopularSlip[];
  events: MatchOffer[];
  /** Picks left out: their match started or the market closed. */
  missing: number;
};

/** "Bet together": saves the slip's prematch picks and answers their code. */
export const createSharedSlip = async (
  legs: PopularSlipLeg[]
): Promise<string | null> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

  try {
    const response = await axios.post(`${baseUrl}/shared_slips`, { legs });
    return response.data?.code ?? null;
  } catch (e) {
    console.error(e);
    return null;
  }
};

/** A friend's shared slip by its code; null when there is no such code. */
export const getSharedSlip = async (
  code: string
): Promise<SharedSlipResponse | null> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

  try {
    const response = await axios.get(
      `${baseUrl}/shared_slips/${encodeURIComponent(code)}`
    );
    const events: MatchOffer[] = [];
    for (let [key, value] of splitEventsBySport(response.data.events ?? [])) {
      events.push(...mapSportEvents(value, key));
    }
    return {
      code: response.data.code,
      slips: response.data.slips ?? [],
      events,
      missing: response.data.missing ?? 0,
    };
  } catch (e) {
    console.error(e);
    return null;
  }
};

/** `withoutOdds`: matches only, no markets - for result lists that show no
 *  odds (mobile search); a short query otherwise pulls ~300 KB of markets. */
export const searchEventsByTeamName = async (
  query: string,
  onlyLive: boolean = false,
  withoutOdds: boolean = false
) => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

  try {
    const normalizedQuery = normalizeTeamSearchQueryForApi(query);
    if (!normalizedQuery) {
      return null;
    }
    const response = await axios.get(
      `${baseUrl}/search?query=${encodeURIComponent(normalizedQuery)}${
        onlyLive ? '&live=True' : withoutOdds ? '&markets=none' : ''
      }`
    );
    let events: Map<number, MatchOffer[] | LiveEvent[]> = new Map();

    if (!onlyLive) {
      const splittedEvents = splitEventsByLeague(response.data.events);
      for (let [key, value] of splittedEvents) {
        events.set(key, mapSportEvents(value, value[0].league.sport));
      }
    } else {
      const splittedEvents = splitLiveEventsBySport(response.data.events);
      for (let [key, value] of splittedEvents) {
        events.set(
          key,
          value
            .filter((x: any) => hasLiveEventStarted(x.results[0]))
            .map((x: any) => mapLiveEvent(x.results[0], key)) as LiveEvent[]
        );
      }
    }

    return events;
  } catch (e) {
    console.error(e);
    return null;
  }
};
